import { isServer } from '@sitecore-jss/sitecore-jss';
import { useBaseDataLayerVariables } from './useBaseDataLayerVariables';
import dayjs from 'dayjs';

/**
 * Processes data layer object, normalizing values and handling special cases
 */
export const processDataLayerObject = (data, preserveUnderscore = true) => {
  if (!data || typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    return data.map((item) => processDataLayerObject(item, preserveUnderscore));
  }

  const processed = {};

  for (const key in data) {
    if (data[key] === undefined || data[key] === null) continue;

    const preventNormalization = preserveUnderscore && key.startsWith('_');
    const normalizedKey = preventNormalization ? key.substring(1) : key;

    let normalizedValue;
    if (preventNormalization) {
      normalizedValue = data[key];
    } else if (typeof data[key] === 'object' && data[key] !== null) {
      normalizedValue = processDataLayerObject(data[key], preserveUnderscore);
    } else {
      normalizedValue = data[key];
    }

    processed[normalizedKey] = normalizedValue;
  }

  return processed;
};

/**
 * Ensures dataLayer is initialized and doesn't execute on server
 * @returns {boolean} True if ready to push to dataLayer, false otherwise
 */
const ensureDataLayerExists = () => {
  if (isServer()) return false;
  window.dataLayer = window.dataLayer || [];
  return true;
};

/**
 * Factory function to create a data layer registration hook for different data layer types
 */
export const createDataLayerRegister = () => {
  return () => {
    const { baseProps, linkProps, pageProps, itemProps, promoItemProps, updateSitecoreContext } =
      useBaseDataLayerVariables();

    /**
     * Registers an action in the data layer with base properties
     * @param {Object} data - The data to register
     * @param {Object} [additionalBaseProps={}] - Additional base properties to merge
     * @returns {Array|undefined} - The current data layer if not on server
     */
    const registerAction = (data = {}, additionalBaseProps = {}) => {
      if (!ensureDataLayerExists()) return;
      updateSitecoreContext();

      const eventData = {
        event: data.event || 'base_event',
        ...baseProps.value,
        timestamp: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        ...additionalBaseProps,
        ...data,
      };

      const finalPayload = processDataLayerObject(eventData, true);

      window.dataLayer.push(finalPayload);
      return window.dataLayer;
    };

    /**
     * Registers a user interaction event reusing registerAction with link properties
     * @param {Object} data - The interaction data
     * @returns {Array|undefined} - The current data layer if not on server
     */
    const registerInteraction = (data = {}) => {
      // Use registerAction, merging linkProps as additional base properties
      return registerAction({ event: data.event || 'user_interaction', ...data }, linkProps.value);
    };

    /**
     * Registers a page view event reusing registerAction with page properties
     * @param {Object} data - The interaction data
     * @returns {Array|undefined} - The current data layer if not on server
     */
    const registerPageView = (data = {}) => {
      // Use registerAction, merging pageProps as additional base properties
      return registerAction({ event: data.event || 'spa_page_view', ...data }, pageProps.value);
    };

    /**
     * Registers a list view event, adding itemProps to each item in ecommerce.items
     * @param {Object} data - The list view data
     * @returns {Array|undefined} - The current data layer if not on server
     */
    const registerListView = (data = {}) => {
      const eventData = {
        event: data.event || 'view_item_list',
        ...baseProps.value,
        ...data,
      };

      // If ecommerce.items is an array, merge itemProps into each item
      if (eventData.ecommerce && Array.isArray(eventData.ecommerce.items)) {
        eventData.ecommerce.items = eventData.ecommerce.items.map((item) => ({
          ...itemProps.value,
          ...item,
        }));
      }

      return registerAction({ ...eventData });
    };

    const registerPromotionEvent = (data = {}) => {
      const eventData = {
        ...baseProps.value,
        ...data,
      };

      if (eventData.ecommerce && Array.isArray(eventData.ecommerce.items)) {
        eventData.ecommerce.items = eventData.ecommerce.items.map((item) => ({
          ...promoItemProps.value,
          ...item,
        }));
      }

      return registerAction({ ...eventData });
    };

    return {
      registerAction,
      registerInteraction,
      registerPageView,
      registerListView,
      registerPromotionEvent,
    };
  };
};

// Create specific data layer registrars
export const useDefaultDataLayer = createDataLayerRegister();
